/**
 * Витрина Dealing Desk — результат клиентов в раскладке по книгам
 * (карточка BL-2026-09-16-90e6).
 *
 * Flow: монтирование → GET /api/dealing/filters наполняет выпадающие списки →
 *       пользователь задаёт период и срезы → «Показать» переносит черновик
 *       фильтров в применённые → три независимых запроса GET /api/dealing/rows
 *       (book=bbook|abook|cent_bbook) → три таблицы одинакового состава
 *       столбцов, под каждой — строка итогов с сервера.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadDealingFilters, loadDealingRows;
 *   - CoverageMatrixPanel.jsx — вторая вкладка раздела: настройка процента
 *     перекрытия (карточка BL-2026-10-09-4f80 §3.2).
 *
 * Границы: ВИТРИНА по-прежнему ничего не меняет — правка перекрытия стоит
 * соседней вкладкой, а не встроена в таблицу. Прежде здесь было записано
 * «экран ничего не меняет» в сильном смысле: матрица правилась только
 * ручкой, экрана не было вовсе. Проверка работающей системы 2026-10-09
 * показала цену этого решения: правил перекрытия ноль, все счета идут как
 * 100 % B-Book, книга A-Book пуста целиком — не потому, что расчёт неверен,
 * а потому что задать перекрытие было неоткуда (техспека 4f80 §2.2).
 * Исходное опасение («смотрящий на цифры может их же и переписать в том же
 * движении») остаётся в силе и снимается РАЗДЕЛЕНИЕМ: вкладки — два разных
 * экрана, и ни одна цифра витрины не правится на месте.
 *
 * ПОЧЕМУ ВКЛАДКА РАЗДЕЛА, А НЕ ОТДЕЛЬНЫЙ РАЗДЕЛ И НЕ «НАСТРОЙКИ». Три
 * причины, каждая самостоятельная:
 *   - правка перекрытия — работа ДИЛИНГА, а роль `dealing` раздел
 *     «Настройки» не видит; спрятав матрицу туда, настройку дилинга отдали
 *     бы тому, кто ею не занимается;
 *   - состав разделов живёт в данных (app.section) и приходит с сервера
 *     полем sections в GET /api/me — завести новый раздел правкой одного
 *     фронтенда нельзя: пункта меню просто не появилось бы;
 *   - вопрос «почему у этого счёта такая раскладка» и ответ на него —
 *     соседние движения, и разные разделы заставляли бы сверять две
 *     страницы по памяти.
 *
 * Вкладка живёт в СОСТОЯНИИ, а не в адресе: router.js сопоставляет адрес с
 * выдачей точным совпадением пути, и `/dealing-desk/coverage` дал бы
 * названный отказ «раздел не выдан». Цена — вкладку нельзя прислать
 * ссылкой; снимается это вложенными маршрутами, а не вторым механизмом
 * маршрутизации рядом с первым.
 *
 * Связанные файлы:
 *   - src/App.jsx — раздел «Dealing Desk» в навигации;
 *   - src/router.js — адрес раздела /dealing-desk;
 *   - src/CoverageMatrixPanel.jsx — вкладка «Матрица перекрытия»;
 *   - docs/specs/BL-2026-09-16-90e6-dealing-desk.md — техспека: §6 витрина,
 *     §7 граница Этапа 1, Приложение А — контракт REST;
 *   - docs/specs/BL-2026-10-09-4f80-dealing-completion.md — §3.2 экран
 *     матрицы, §4 новые ручки, §5 приёмка 2 и 3.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { loadDealingFilters, loadDealingRows } from './api'
import CoverageMatrixPanel from './CoverageMatrixPanel'

/**
 * Семантика окна расчёта у столбца.
 *
 * Маркер у заголовка — это не украшение, а единственный способ узнать, за
 * что отвечает цифра: «Profit» за период и «All_Realized_PL» за всё время
 * стоят рядом и выглядят одинаково (техспека §6.3). Значения маркеров
 * объявлены здесь ОДИН раз и подставляются из метаданных столбца — руками
 * у двадцати девяти заголовков они разошлись бы при первой же правке.
 */
export const WINDOW_MARKERS = {
  period: { mark: '🔴', title: 'считается за выбранный период' },
  all: { mark: '🟢', title: 'за всё время либо состояние онлайн' },
  money: { mark: '🟡', title: 'денежные движения за период' },
}

/**
 * Выравнивание выводится из формата, а не пишется у каждого столбца.
 *
 * Правило одно: вправо стоит всё, что число. Повторённое двадцать раз, оно
 * превратилось бы в двадцать мест, где можно ошибиться, — и ошибка в одном
 * из них выглядит как случайно съехавший столбец, а не как дефект.
 */
const FORMAT_ALIGN = {
  login: 'num',
  money: 'num',
  pct: 'num',
  lots: 'num',
  mio: 'num',
  text: 'left',
  first_deal: 'left',
}

/**
 * Состав столбцов витрины — ОДНО объявление данными (техспека §6.3).
 *
 * Поля:
 *   key    — поле строки ответа /api/dealing/rows;
 *   title  — заголовок; оставлен латиницей ровно как в макетах: этими
 *            именами дилинг пользуется в своих выгрузках, и перевод на
 *            русский сделал бы экран и выгрузку двумя разными отчётами;
 *   window — окно расчёта, ключ WINDOW_MARKERS; null — окна нет (реквизит);
 *   format — как показывать значение (см. formatCell).
 *
 * Тот же приём, что у перечня маршрутов backend-api (routes.go,
 * BL-2026-08-15-9c63): состав, объявленный данными, обходится тестом и не
 * может разойтись с тем, что рисуется, — потому что рисуется из него же.
 * Все три таблицы берут ЭТОТ перечень: одинаковость их состава — требование
 * §6.2, а не совпадение, и три копии разметки его бы не удержали.
 */
export const DEALING_COLUMNS = [
  { key: 'login', title: 'Login', window: null, format: 'login' },
  { key: 'name', title: 'Name', window: null, format: 'text' },
  { key: 'proverka', title: 'Proverka', window: null, format: 'text' },
  { key: 'group_now', title: 'Group_NOW', window: 'all', format: 'text' },
  { key: 'realized_swap', title: 'Realized_Swap', window: 'period', format: 'money' },
  { key: 'commission', title: 'Commission', window: 'period', format: 'money' },
  { key: 'dividend', title: 'Dividend', window: 'period', format: 'money' },
  { key: 'korrekcii', title: 'Korrekcii', window: 'period', format: 'money' },
  { key: 'profit', title: 'Profit', window: 'period', format: 'money' },
  { key: 'floating_pl', title: 'Floating_PL', window: 'period', format: 'money' },
  {
    key: 'floating_pl_plus_all_pl',
    title: 'Floating_PL_plus_all_PL',
    window: 'period',
    format: 'money',
  },
  { key: 'gain_pct_all_fl', title: 'Gain_%_ALL_FL', window: 'all', format: 'pct' },
  { key: 'pervaya_sdelka', title: 'pervaya_sdelka', window: 'all', format: 'first_deal' },
  { key: 'byla_proverka', title: 'Byla proverka', window: null, format: 'text' },
  { key: 'gain_pct_all', title: 'Gain_%_ALL', window: 'all', format: 'pct' },
  { key: 'all_realized_pl', title: 'All_Realized_PL', window: 'all', format: 'money' },
  { key: 'gain_pct_the_period', title: 'Gain_%_the_period', window: 'period', format: 'pct' },
  { key: 'nominal_book_vol', title: 'Nominal_BOOK_VOL', window: 'period', format: 'mio' },
  { key: 'polny_obiom_lot', title: 'polny_obiom_Lot', window: 'period', format: 'lots' },
  { key: 'close_obiom_lot', title: 'close_obiom_Lot', window: 'period', format: 'lots' },
  { key: 'balance_online', title: 'Balance_online', window: 'all', format: 'money' },
  { key: 'group', title: 'Group', window: 'all', format: 'text' },
  { key: 'country', title: 'Country', window: null, format: 'text' },
  { key: 'lk', title: 'LK', window: null, format: 'text' },
  { key: 'agent', title: 'Agent', window: null, format: 'text' },
  { key: 'cash_in', title: 'Cash_in', window: 'money', format: 'money' },
  { key: 'cash_out', title: 'Cash_out', window: 'money', format: 'money' },
  { key: 'cash_in_plus_out', title: 'Cash_in_plus_out', window: 'money', format: 'money' },
  { key: 'cash_in_plus_out_all', title: 'Cash_in_plus_out_ALL', window: 'all', format: 'money' },
]

/**
 * Три среза витрины (техспека §6.2). Тоже перечень данными: заголовки и
 * коды книг заданы рядом, и добавление четвёртого среза — одна строка.
 */
export const BOOKS = [
  { book: 'bbook', title: 'B-BOOK часть (в том числе частичный хедж), CLIENT SIDE' },
  { book: 'abook', title: 'A-BOOK часть (в том числе частичный хедж), CLIENT SIDE' },
  { book: 'cent_bbook', title: 'Cent B-BOOK (всё в USD), CLIENT SIDE' },
]

/**
 * Откуда взялась раскладка строки по книгам (Приложение А техспеки).
 *
 * `default` — это НЕ разновидность расчёта, а признание, что фактической
 * истории перекрытия за период нет и применено умолчание BRD §5.3. Без
 * пометки допущение становится невидимой ошибкой (техспека §7).
 */
export const COVERAGE_TITLES = {
  rule: 'посчитано по действующей матрице перекрытия',
  snapshot: 'посчитано по накопленной истории интервалов',
  default:
    'фактической истории перекрытия за период нет: принято, что группа не отступала ' +
    'от базовой установки и разбивки по символам внутри логина не было (§5.3)',
}

/**
 * Разделитель разрядов — апостроф, как в макетах дилинга (8’757.00).
 *
 * Записан escape-последовательностью: в исходнике типографский апостроф
 * неотличим от обычного, и подмена одного другим прошла бы незамеченной.
 */
const GROUP_SEP = '’'

/** Денежная строка: знак, целая часть, необязательная дробная. */
const MONEY_RE = /^([+-]?)(\d+)(\.\d+)?$/

/**
 * Показывает денежную величину, пришедшую СТРОКОЙ.
 *
 * @param {string|null} value — величина в десятичной записи фиксированной точности
 * @returns {string} число с разделителями разрядов либо «—»
 *
 * Работает НАД СТРОКОЙ и ни на шаг не заходит в Number: значение проходит
 * от сервера до экрана без единого преобразования через double. Причина —
 * в Приложении А техспеки: раскладка по книгам обязана сходиться до копейки,
 * а `A-Book + B-Book = итог счёта` — проверяемая аксиома (§5.2). Дробная
 * часть переносится как есть, поэтому центовая книга с четырьмя знаками
 * (0.0970) показывается четырьмя, а долларовая двумя — точность задаёт
 * валюта, а не экран.
 *
 * Разряды отбиваются у ВСЕХ денежных столбцов. В макетах это соблюдено не
 * везде (Balance_online с апострофом, Cash_in_plus_out_ALL без) — но макеты
 * сняты с таблицы, где формат задан по ячейкам, а на экране одинаковые по
 * смыслу столбцы обязаны читаться одинаково.
 *
 * Величина, которой нет, показывается прочерком, а НЕ нулём: ноль в
 * финансовом отчёте читается как факт «движений не было» (BR-011).
 */
export function formatDealingMoney(value) {
  if (value === null || value === undefined) return '—'
  const s = String(value).trim()
  if (s === '') return '—'
  const m = MONEY_RE.exec(s)
  // Не разобралось как десятичное число — показываем как пришло. Молча
  // подставленный прочерк скрыл бы дефект контракта.
  if (!m) return s
  const [, sign, whole, frac] = m
  return sign + whole.replace(/\B(?=(\d{3})+$)/g, GROUP_SEP) + (frac || '')
}

/**
 * Показывает нечисловую по смыслу величину: процент доходности, объём в лотах.
 *
 * @param {string|number|null} value
 * @returns {string} значение как есть либо «—»
 *
 * Разряды здесь НЕ отбиваются: проценты и лоты — величины малого порядка,
 * и апостроф в «1404.03» только мешал бы. И так же, как у денег, значение
 * не пропускается через Number — сервер уже задал нужное число знаков.
 */
export function formatDealingPlain(value) {
  if (value === null || value === undefined) return '—'
  const s = String(value).trim()
  return s === '' ? '—' : s
}

/** Номинальный объём книги: в макетах он подписан единицей — «0.007 mio». */
export function formatMio(value) {
  const s = formatDealingPlain(value)
  return s === '—' ? s : `${s} mio`
}

/**
 * Пороги давности первой сделки, дни. Взяты с макетов: дилинг читает не
 * дату, а разряд давности — «клиент новый / клиент давний».
 */
const FIRST_DEAL_BUCKETS = [3, 14, 60, 180, 360]

/**
 * Показывает давность первой сделки разрядом, а не датой.
 *
 * @param {string|null} iso — момент первой сделки в UTC
 * @param {number} now — точка отсчёта, мс (подменяется в тестах)
 * @returns {string} «< 14 дней назад», «> 360 дней назад» либо «—»
 */
export function formatFirstDeal(iso, now = Date.now()) {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return String(iso)
  const days = (now - t) / 86400000
  const bucket = FIRST_DEAL_BUCKETS.find((d) => days < d)
  return bucket === undefined ? '> 360 дней назад' : `< ${bucket} дней назад`
}

/**
 * Показывает момент в UTC — время системы едино (LR-005).
 *
 * Своя копия, а не импорт из DashboardPanel: панели не должны зависеть друг
 * от друга ради трёх строк форматирования — так же поступает и блок
 * расхождений сверки (formatBreakMoment).
 */
export function formatDealingMoment(iso) {
  if (!iso) return 'момент неизвестен'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
}

/**
 * Показывает давность данных словами.
 *
 * @param {string|null} iso — момент среза (as_of из ответа)
 * @param {number} now — точка отсчёта, мс
 * @returns {string} «12 мин назад», «3 ч назад», «2 сут назад»
 *
 * Нужна ровно ради пятого критерия приёмки: когда менеджерский API
 * недоступен, экран обязан показать последние загруженные данные С ОТМЕТКОЙ
 * ДАВНОСТИ, а не пустоту. Отметка словами, а не голой датой: «14:30 UTC»
 * не отвечает на вопрос «это свежее или вчерашнее».
 */
export function formatDataAge(iso, now = Date.now()) {
  if (!iso) return 'давность неизвестна'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return 'давность неизвестна'
  const minutes = Math.max(0, Math.round((now - t) / 60000))
  if (minutes < 90) return `${minutes} мин назад`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} ч назад`
  return `${Math.round(hours / 24)} сут назад`
}

/**
 * Показывает значение ячейки по метаданным столбца.
 *
 * @param {object} column — элемент DEALING_COLUMNS
 * @param {*} value — значение поля строки
 * @returns {string} готовый к показу текст
 */
export function formatCell(column, value) {
  switch (column.format) {
    case 'money':
      return formatDealingMoney(value)
    case 'mio':
      return formatMio(value)
    case 'first_deal':
      return formatFirstDeal(value)
    case 'pct':
    case 'lots':
      return formatDealingPlain(value)
    default:
      // Реквизиты (имя, группа, страна, отметка проверки). `proverka` и
      // `byla_proverka` приходят null до ответа на вопрос §10.3 техспеки —
      // и показываются прочерком, а не выдуманным «Нет»: чего система не
      // знает, того она не утверждает.
      return value === null || value === undefined || value === ''
        ? '—'
        : String(value)
  }
}

/**
 * Фильтры по умолчанию: текущие сутки UTC целиком.
 *
 * @param {Date} now — текущий момент (подменяется в тестах)
 * @returns {object} черновик фильтров
 *
 * Сутки берутся в UTC, а не в поясе браузера: всё хранение и сравнение в
 * проекте идёт в UTC (LR-005), и «сегодня» у дилинга в Москве и у смотрящего
 * из другой страны обязано означать один и тот же отрезок.
 */
export function defaultDealingFilters(now = new Date()) {
  const day = now.toISOString().slice(0, 10)
  return {
    from: `${day}T00:00`,
    to: `${day}T23:59`,
    login: '',
    country: [],
    state: [],
    agent: [],
    group: [],
    currency: [],
    nbp: false,
    to_usd: false,
  }
}

/**
 * Переводит значение поля <input type="datetime-local"> в момент UTC.
 *
 * @param {string} value — 'YYYY-MM-DDTHH:mm'
 * @param {boolean} isEnd — это конец периода
 * @returns {string} ISO-момент с суффиксом Z либо пустая строка
 *
 * Введённое трактуется КАК UTC, и поля подписаны «UTC». Браузерное
 * datetime-local не несёт зоны, и молчаливый перевод из местного пояса
 * сдвинул бы период на несколько часов — то самое расхождение, от которого
 * бережёт показ моментов в UTC на всех остальных экранах.
 *
 * Конец периода включает выбранную минуту ЦЕЛИКОМ (:59): иначе сделки
 * последней минуты суток выпадали бы из отчёта, и выпадали бы молча.
 */
export function utcIso(value, isEnd = false) {
  if (!value) return ''
  return `${String(value).slice(0, 16)}:${isEnd ? '59' : '00'}Z`
}

/** Мультивыборные поля отбора: имя параметра запроса совпадает с полем фильтров. */
const MULTI_FIELDS = ['country', 'state', 'agent', 'group', 'currency']

/**
 * Собирает параметры запроса /api/dealing/rows из фильтров.
 *
 * @param {object} filters — состояние панели фильтров
 * @returns {object} параметры запроса без `book` — его подставляет таблица
 *
 * Мультивыбор склеивается запятой по контракту (Приложение А). Пустые срезы
 * НЕ отправляются вовсе: `country=` означало бы «страна — пустая строка»,
 * а нужно «по стране не отбираем».
 *
 * Выключенные чекбоксы тоже не отправляются: `nbp=false` и отсутствие
 * параметра для сервера одно и то же, но короткий адрес читаемее в журнале.
 */
export function dealingQuery(filters) {
  const q = { from: utcIso(filters.from), to: utcIso(filters.to, true) }
  const login = String(filters.login || '').trim()
  if (login) q.login = login
  for (const field of MULTI_FIELDS) {
    const chosen = filters[field] || []
    if (chosen.length > 0) q[field] = chosen.join(',')
  }
  if (filters.nbp) q.nbp = 'true'
  if (filters.to_usd) q.to_usd = 'true'
  return q
}

/**
 * Пометка происхождения раскладки рядом со значением Group_NOW.
 *
 * @param {string} source — coverage_source строки
 *
 * Показывается НЕ отдельным столбцом: столбцов и так двадцать девять, и
 * тридцатый со словом «rule» в каждой строке отобрал бы место у данных.
 * Пометка стоит там, где стоит сама раскладка, — у книги и процента.
 *
 * `rule` не помечается вовсе: это норма, а пометка на каждой строке в норме
 * перестаёт замечаться и через неделю не заметится и на строке-исключении.
 * `default` помечается предупреждающим цветом и попадает в счётчик над
 * таблицей — так его нельзя не увидеть, не крася при этом весь экран.
 */
export function CoverageMark({ source }) {
  if (source === 'default') {
    return (
      <sup className="coverage coverage-default" title={COVERAGE_TITLES.default}>
        допущение
      </sup>
    )
  }
  if (source === 'snapshot') {
    return (
      <sup className="coverage coverage-snapshot" title={COVERAGE_TITLES.snapshot}>
        история
      </sup>
    )
  }
  return null
}

/**
 * Отметка свежести данных витрины.
 *
 * @param {string|null} asOf — момент среза
 * @param {boolean} stale — источник не отвечает, данные последние загруженные
 *
 * Пятый критерий приёмки: недоступный менеджерский API даёт не пустой экран,
 * а прежние цифры с честной подписью, насколько они старые.
 */
export function DataAge({ asOf, stale }) {
  return (
    <span className={stale ? 'warn' : 'muted'}>
      данные на {formatDealingMoment(asOf)} · {formatDataAge(asOf)}
      {stale && (
        <>
          {' '}
          <span className="badge badge-warn" title="показаны последние загруженные данные">
            источник не отвечает
          </span>
        </>
      )}
    </span>
  )
}

/**
 * Заголовок столбца с маркером окна расчёта.
 *
 * Маркер подставляется из метаданных столбца (WINDOW_MARKERS), а не пишется
 * у заголовка руками. Пояснение маркера — в title: цветной кружок без
 * расшифровки сам по себе ничего не сообщает.
 */
function ColumnHead({ column }) {
  const marker = column.window ? WINDOW_MARKERS[column.window] : null
  const align = FORMAT_ALIGN[column.format] === 'num' ? 'num' : ''
  return (
    <th className={align} title={marker ? `${column.title}: ${marker.title}` : column.title}>
      {column.title}
      {marker && (
        <span className="window-mark" aria-label={marker.title}>
          {marker.mark}
        </span>
      )}
    </th>
  )
}

/**
 * Одна из трёх таблиц витрины.
 *
 * @param {string} book — код книги для параметра запроса
 * @param {string} title — заголовок таблицы
 * @param {object} filters — ПРИМЕНЁННЫЕ фильтры; смена ссылки перезапрашивает
 *
 * Побочный эффект: GET /api/dealing/rows при монтировании и при каждой смене
 * применённых фильтров.
 *
 * Своё состояние загрузки у каждой таблицы, а не одно на три: книги считаются
 * независимо, и готовая B-Book показывается, не дожидаясь центовой.
 */
function BookTable({ book, title, filters }) {
  const [state, setState] = useState({ status: 'loading' })

  useEffect(() => {
    // Отменяем применение ответа, если фильтры сменились, пока он летел:
    // иначе запоздавший ответ прежнего отбора затёр бы цифры нового.
    let cancelled = false
    setState({ status: 'loading' })
    loadDealingRows(book, dealingQuery(filters))
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data })
      })
      .catch((e) => {
        if (!cancelled) setState({ status: 'error', message: e.message })
      })
    return () => {
      cancelled = true
    }
  }, [book, filters])

  if (state.status === 'loading') {
    return (
      <section className="block">
        <h2>{title}</h2>
        <p className="muted">Загрузка…</p>
      </section>
    )
  }

  if (state.status === 'error') {
    return (
      <section className="block">
        <h2>{title}</h2>
        <p className="error">Не загрузилась: {state.message}</p>
      </section>
    )
  }

  const { rows = [], totals, as_of: asOf, stale } = state.data
  const assumed = rows.filter((r) => r.coverage_source === 'default').length

  return (
    <section className="block">
      <div className="dashboard-head">
        <h2>{title}</h2>
        <DataAge asOf={asOf} stale={stale} />
      </div>

      {assumed > 0 && (
        <p className="notice notice-slim">
          Посчитано допущением: {assumed} из {rows.length} строк — фактической истории
          перекрытия за период нет (§5.3 BRD). Такие строки помечены у Group_NOW.
        </p>
      )}

      {rows.length === 0 ? (
        <p className="muted">
          За выбранный период строк нет. Пустая выдача — это не поломка: доступ
          менеджера ограничен группами, и отбор мог не совпасть ни с одной из них.
        </p>
      ) : (
        <div className="dealing-wrap">
          <table className="dealing">
            <thead>
              <tr>
                {DEALING_COLUMNS.map((c) => (
                  <ColumnHead key={c.key} column={c} />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.login}>
                  {DEALING_COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className={FORMAT_ALIGN[c.format] === 'num' ? 'num' : ''}
                    >
                      {formatCell(c, row[c.key])}
                      {c.key === 'group_now' && (
                        <CoverageMark source={row.coverage_source} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {totals && (
              <tfoot>
                <tr>
                  {DEALING_COLUMNS.map((c, index) => {
                    // Первая клетка подписывает строку; дальше — те поля,
                    // которые сервер прислал в totals. Что суммировать, решает
                    // ОН: доходность в процентах не складывается, и клетка под
                    // ней остаётся пустой сама собой, без списка исключений здесь.
                    if (index === 0) return <th key={c.key}>Итого</th>
                    const value = totals[c.key]
                    return (
                      <th
                        key={c.key}
                        className={FORMAT_ALIGN[c.format] === 'num' ? 'num' : ''}
                      >
                        {value === undefined ? '' : formatCell(c, value)}
                      </th>
                    )
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  )
}

/**
 * Поле мультивыбора среза.
 *
 * @param {string} label — подпись
 * @param {Array<string>} options — значения из /api/dealing/filters
 * @param {Array<string>} value — выбранные значения
 * @param {function} onChange — принимает новый массив значений
 *
 * Обычный <select multiple>, а не библиотечный виджет: набор значений мал
 * и приходит с сервера готовым, а зависимость ради выпадающего списка
 * пришлось бы сопровождать (та же мера, что в router.js и api.js).
 */
function MultiSelect({ label, options, value, onChange }) {
  return (
    <label className="filter">
      <span>{label}</span>
      <select
        multiple
        size={4}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * Верхняя панель фильтров (техспека §6.1).
 *
 * @param {object} draft — черновик фильтров
 * @param {function} onDraft — обновление черновика
 * @param {object} lists — значения списков с сервера
 * @param {function} onApply — перенести черновик в применённые
 *
 * Черновик отделён от применённых фильтров намеренно: три запроса на каждое
 * нажатие клавиши в поле логина — это три отменённых ответа и лишняя работа
 * сервера, который считает раскладку по книгам, а не отдаёт готовую строку.
 */
function FiltersBar({ draft, onDraft, lists, onApply }) {
  const set = (field) => (value) => onDraft({ ...draft, [field]: value })

  return (
    <form
      className="dealing-filters"
      onSubmit={(e) => {
        e.preventDefault()
        onApply()
      }}
    >
      <label className="filter">
        <span>Начало, UTC</span>
        <input
          type="datetime-local"
          value={draft.from}
          onChange={(e) => set('from')(e.target.value)}
        />
      </label>
      <label className="filter">
        <span>Конец, UTC</span>
        <input
          type="datetime-local"
          value={draft.to}
          onChange={(e) => set('to')(e.target.value)}
        />
      </label>
      <label className="filter">
        <span>Login</span>
        <input
          type="text"
          inputMode="numeric"
          placeholder="все"
          value={draft.login}
          onChange={(e) => set('login')(e.target.value)}
        />
      </label>

      <MultiSelect
        label="Страна"
        options={lists.countries}
        value={draft.country}
        onChange={set('country')}
      />
      <MultiSelect
        label="ЛК / State"
        options={lists.states}
        value={draft.state}
        onChange={set('state')}
      />
      <MultiSelect
        label="Партнёр / Agent"
        options={lists.agents}
        value={draft.agent}
        onChange={set('agent')}
      />
      <MultiSelect
        label="Группы"
        options={lists.groups}
        value={draft.group}
        onChange={set('group')}
      />
      <MultiSelect
        label="Валюта счетов"
        options={lists.currencies}
        value={draft.currency}
        onChange={set('currency')}
      />

      <div className="filter filter-checks">
        <label className="check">
          <input
            type="checkbox"
            checked={draft.nbp}
            onChange={(e) => set('nbp')(e.target.checked)}
          />
          <span>Не учитывать уход баланса счёта ниже 0</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={draft.to_usd}
            onChange={(e) => set('to_usd')(e.target.checked)}
          />
          <span>Конвертация всех счетов в USD формат</span>
        </label>
        <button type="submit">Показать</button>
      </div>
    </form>
  )
}

/** Пустые списки до ответа сервера: поля отбора рисуются сразу, но пустыми. */
const EMPTY_LISTS = { groups: [], countries: [], states: [], agents: [], currencies: [] }

/**
 * Вкладки раздела — перечень данными, как и всё остальное на этом экране.
 *
 * Их две, и они отвечают на разные вопросы: «какой результат у клиентов»
 * (витрина) и «по какому проценту он разложен» (матрица). Разделение не
 * косметическое: витрина только читает, матрица пишет СНЕПШОТ перекрытия,
 * из которого потом считается историческая раскладка, — и смешивать на
 * одном полотне отчёт с правкой истории нельзя.
 */
export const DEALING_TABS = [
  { id: 'rows', title: 'Витрина по книгам' },
  { id: 'coverage', title: 'Матрица перекрытия' },
]

/**
 * Витрина целиком: фильтры и три таблицы.
 *
 * Побочный эффект: GET /api/dealing/filters при монтировании.
 *
 * Выделена из раздела отдельным компонентом, чтобы переключение вкладки
 * РАЗМОНТИРОВАЛО её: иначе три запроса витрины продолжали бы висеть, пока
 * человек правит матрицу, а вернувшись, он увидел бы цифры, посчитанные до
 * правки, и принял бы их за результат правки.
 */
function RowsTab() {
  const initial = useMemo(() => defaultDealingFilters(), [])
  const [draft, setDraft] = useState(initial)
  const [applied, setApplied] = useState(initial)
  const [lists, setLists] = useState(EMPTY_LISTS)
  const [listsError, setListsError] = useState('')

  useEffect(() => {
    loadDealingFilters()
      .then((data) => setLists({ ...EMPTY_LISTS, ...data }))
      .catch((e) => setListsError(e.message))
  }, [])

  // Новая ССЫЛКА на объект фильтров — сигнал таблицам перезапросить строки.
  // Отдельная функция, а не setApplied(draft) в разметке: draft может быть
  // тем же объектом, и тогда таблицы не заметили бы нажатия.
  const apply = useCallback(() => setApplied({ ...draft }), [draft])

  return (
    <>
      {listsError && (
        <p className="error">
          Списки отбора не загрузились: {listsError}. Отбор по периоду и логину
          работает — срезы по стране, ЛК, партнёру, группам и валюте недоступны.
        </p>
      )}

      <FiltersBar draft={draft} onDraft={setDraft} lists={lists} onApply={apply} />

      {BOOKS.map((b) => (
        <BookTable key={b.book} book={b.book} title={b.title} filters={applied} />
      ))}
    </>
  )
}

/**
 * Переключатель вкладок раздела.
 *
 * @param {string} active — идентификатор текущей вкладки
 * @param {function} onPick — принимает идентификатор выбранной вкладки
 *
 * Кнопки, а не ссылки <a href>, — в отличие от разделов в хедере. Разница
 * не в оформлении: у раздела есть адрес, который можно прислать коллеге, а
 * у вкладки его нет (см. заголовок файла). Ссылка без адреса обещала бы то,
 * чего не умеет: «открыть в новой вкладке» привело бы на витрину.
 *
 * role="tablist" и aria-selected — не украшение: без них переключатель для
 * чтения с экрана выглядит двумя кнопками без признака, какая из них
 * сейчас действует.
 */
function TabBar({ active, onPick }) {
  return (
    <div className="tabs" role="tablist" aria-label="Вкладки раздела Dealing Desk">
      {DEALING_TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={t.id === active}
          className={t.id === active ? 'tab tab-active' : 'tab'}
          onClick={() => onPick(t.id)}
        >
          {t.title}
        </button>
      ))}
    </div>
  )
}

/**
 * Раздел Dealing Desk целиком: заголовок, вкладки, тело текущей вкладки.
 *
 * Свойства разделу не нужны: обе вкладки берут всё, что им требуется, из
 * ответов сервера. Роль пользователя сюда НЕ передаётся намеренно — право
 * править матрицу приходит признаком в теле GET /api/dealing/coverage-rules
 * (см. editRight в CoverageMatrixPanel.jsx), а выведенное из названия роли
 * стало бы третьей копией решения о правах после предиката в Go и перечня
 * маршрутов.
 *
 * Открывается ВИТРИНОЙ, а не матрицей: в раздел приходят смотреть результат,
 * а настройку правят изредка. Вкладка по умолчанию — это ответ на вопрос
 * «зачем сюда чаще всего заходят».
 */
export default function DealingDeskPanel() {
  const [tab, setTab] = useState(DEALING_TABS[0].id)

  return (
    <div className="dashboard">
      <div className="dashboard-head">
        <h1>Dealing Desk</h1>
        <span className="muted">
          Результат клиентов в раскладке по книгам: B-Book — риск брокера,
          A-Book — перекрыто у провайдера ликвидности, Cent — центовые счета
          в долларовой размерности.
        </span>
      </div>

      <TabBar active={tab} onPick={setTab} />

      {tab === 'coverage' ? <CoverageMatrixPanel /> : <RowsTab />}
    </div>
  )
}
